import { jsPDF } from 'jspdf'
import { supabase } from './supabase.js'

const money=(value,currency='USD')=>new Intl.NumberFormat('en-US',{style:'currency',currency:currency||'USD'}).format(Number(value||0))
const clean=v=>String(v??'').trim()
const titleCase=v=>clean(v).replaceAll('_',' ').replace(/\b\w/g,c=>c.toUpperCase())
const dateText=v=>{if(!v)return '—';const d=new Date(v);return Number.isNaN(d.getTime())?String(v):d.toLocaleDateString()}
const addressLines=a=>{if(!a)return[];const city=[a.city,a.state_region,a.postal_code].filter(Boolean).join(', ');return[a.name,a.company,a.address1,a.address2,city,a.country_code].filter(Boolean).map(String)}
const hexToRgb=hex=>{const raw=clean(hex).replace('#','');if(!/^[0-9a-fA-F]{6}$/.test(raw))return[12,18,25];return[parseInt(raw.slice(0,2),16),parseInt(raw.slice(2,4),16),parseInt(raw.slice(4,6),16)]}

async function imageData(url){
  if(!url)return null
  try{
    const response=await fetch(url,{mode:'cors'})
    if(!response.ok)return null
    const blob=await response.blob()
    return await new Promise(resolve=>{const reader=new FileReader();reader.onload=()=>resolve(reader.result);reader.onerror=()=>resolve(null);reader.readAsDataURL(blob)})
  }catch{return null}
}

export async function loadDocumentBranding(tenantId){
  const[t,s]=await Promise.all([
    supabase.from('tenants').select('id,name').eq('id',tenantId).single(),
    supabase.from('tenant_settings').select('logo_url,company_website,support_email,brand_settings').eq('tenant_id',tenantId).maybeSingle()
  ])
  if(t.error)throw t.error
  if(s.error)throw s.error
  const settings=s.data||{},brand=settings.brand_settings||{}
  return{
    companyName:brand.pdf_company_name||t.data?.name||'Business',
    logoUrl:brand.pdf_logo_url||settings.logo_url||'',
    accentColor:brand.pdf_accent_color||'#0c1219',
    address1:brand.pdf_address1||'',
    address2:brand.pdf_address2||'',
    city:brand.pdf_city||'',
    state:brand.pdf_state||'',
    postalCode:brand.pdf_postal_code||'',
    phone:brand.pdf_phone||'',
    email:brand.pdf_email||settings.support_email||'',
    website:brand.pdf_website||settings.company_website||'',
    footer:brand.pdf_footer||'',
    showRunvq:brand.pdf_show_runvq_footer!==false
  }
}

async function loadCustomer(tenantId,accountId,contactId){
  const[a,c]=await Promise.all([
    accountId?supabase.from('accounts').select('id,name,billing_email,main_phone,billing_address,shipping_address').eq('tenant_id',tenantId).eq('id',accountId).maybeSingle():Promise.resolve({data:null,error:null}),
    contactId?supabase.from('contacts').select('id,first_name,last_name,email,phone').eq('tenant_id',tenantId).eq('id',contactId).maybeSingle():Promise.resolve({data:null,error:null})
  ])
  if(a.error)throw a.error;if(c.error)throw c.error
  return{account:a.data,contact:c.data}
}

function customerName(account,contact,fallback='Customer'){
  const person=[contact?.first_name,contact?.last_name].filter(Boolean).join(' ')
  return person||account?.name||fallback
}

function addLines(doc,lines,x,y,{fontSize=9,lineHeight=12,maxWidth=240,bold=false}={}){
  doc.setFont('helvetica',bold?'bold':'normal');doc.setFontSize(fontSize)
  let cy=y
  for(const line of lines.filter(Boolean)){
    const wrapped=doc.splitTextToSize(String(line),maxWidth)
    doc.text(wrapped,x,cy);cy+=wrapped.length*lineHeight
  }
  return cy
}

async function renderCommercialPdf({tenantId,type,record,lines,account,contact,order}){
  const brand=await loadDocumentBranding(tenantId)
  const doc=new jsPDF({unit:'pt',format:'letter',orientation:'portrait'})
  const W=doc.internal.pageSize.getWidth(),H=doc.internal.pageSize.getHeight(),margin=42
  const accent=hexToRgb(brand.accentColor)
  const currency=record.currency_code||'USD'
  const number=record.quote_number||record.order_number||record.invoice_number||record.id
  const filename=`${type}-${String(number).replace(/[^a-z0-9_-]+/gi,'-')}.pdf`
  const logo=await imageData(brand.logoUrl)

  const footer=()=>{
    doc.setDrawColor(220);doc.line(margin,H-40,W-margin,H-40)
    doc.setFont('helvetica','normal');doc.setFontSize(8);doc.setTextColor(105)
    const left=brand.footer||[brand.phone,brand.email,brand.website].filter(Boolean).join(' • ')
    if(left)doc.text(doc.splitTextToSize(left,360),margin,H-24)
    if(brand.showRunvq)doc.text('Powered by RUNVQ',W-margin,H-24,{align:'right'})
    doc.setTextColor(0)
  }
  const pageHeader=()=>{doc.setFillColor(...accent);doc.rect(0,0,W,8,'F')}
  const newPage=()=>{footer();doc.addPage();pageHeader();return 48}
  pageHeader()

  let y=42
  if(logo){try{doc.addImage(logo,'AUTO',margin,y,105,46,undefined,'FAST')}catch{}}
  const companyX=logo?165:margin
  doc.setFont('helvetica','bold');doc.setFontSize(18);doc.text(brand.companyName,companyX,y+15)
  const companyAddress=[brand.address1,brand.address2,[brand.city,brand.state,brand.postalCode].filter(Boolean).join(', ')].filter(Boolean)
  addLines(doc,[...companyAddress,[brand.phone,brand.email].filter(Boolean).join(' • '),brand.website],companyX,y+31,{fontSize:8,lineHeight:10,maxWidth:260})

  doc.setTextColor(...accent);doc.setFont('helvetica','bold');doc.setFontSize(24);doc.text(type.toUpperCase(),W-margin,y+12,{align:'right'})
  doc.setTextColor(0);doc.setFontSize(12);doc.text(String(number),W-margin,y+31,{align:'right'})
  y=112

  const meta=[]
  if(type==='Quote'){meta.push(['Created',dateText(record.created_at)],['Valid Until',record.valid_until||'—'],['Status',titleCase(record.status)],['Terms',record.payment_terms_code||'—'])}
  if(type==='Order'){meta.push(['Ordered',dateText(record.order_date)],['Status',titleCase(record.status)],['PO',record.customer_po_number||'—'],['Terms',record.payment_terms_code||'—'])}
  if(type==='Invoice'){meta.push(['Invoice Date',record.invoice_date||dateText(record.created_at)],['Due Date',record.due_date||'—'],['Status',titleCase(record.status)],['Terms',record.payment_terms_code||'—'])}
  doc.setFillColor(246,247,249);doc.roundedRect(margin,y,W-margin*2,54,5,5,'F')
  const cellW=(W-margin*2)/meta.length
  meta.forEach(([label,value],i)=>{const x=margin+i*cellW+12;doc.setFont('helvetica','normal');doc.setFontSize(7);doc.setTextColor(100);doc.text(label.toUpperCase(),x,y+17);doc.setFont('helvetica','bold');doc.setFontSize(9);doc.setTextColor(20);doc.text(doc.splitTextToSize(String(value),cellW-20),x,y+34)})
  y+=78

  const fallbackName=record.customer_name_snapshot||account?.name||'Customer'
  const name=customerName(account,contact,fallbackName)
  const email=record.customer_email_snapshot||contact?.email||account?.billing_email||''
  const phone=record.customer_phone_snapshot||contact?.phone||account?.main_phone||''
  const billing=record.billing_address||order?.billing_address||account?.billing_address||null
  const shipping=record.shipping_address||order?.shipping_address||account?.shipping_address||billing

  doc.setFont('helvetica','bold');doc.setFontSize(9);doc.setTextColor(...accent);doc.text('CUSTOMER',margin,y)
  doc.text('SHIP TO',315,y)
  doc.setTextColor(0)
  let leftY=addLines(doc,[name,email,phone,...addressLines(billing)],margin,y+18,{fontSize:9,lineHeight:11,maxWidth:235})
  let rightY=addLines(doc,addressLines(shipping),315,y+18,{fontSize:9,lineHeight:11,maxWidth:240})
  y=Math.max(leftY,rightY)+15

  if(record.customer_po_number){doc.setFont('helvetica','bold');doc.setFontSize(8);doc.text(`CUSTOMER PO: ${record.customer_po_number}`,margin,y);y+=18}
  if(record.requested_delivery_date){doc.setFont('helvetica','normal');doc.setFontSize(8);doc.text(`Requested delivery: ${record.requested_delivery_date}`,margin,y);y+=18}

  const rowH=24
  const columns=[{x:margin,w:270,label:'Description',align:'left'},{x:315,w:45,label:'Qty',align:'right'},{x:370,w:80,label:'Unit',align:'right'},{x:462,w:108,label:'Total',align:'right'}]
  const tableHeader=()=>{doc.setFillColor(...accent);doc.rect(margin,y,W-margin*2,22,'F');doc.setTextColor(255);doc.setFont('helvetica','bold');doc.setFontSize(8);columns.forEach(c=>doc.text(c.label,c.align==='right'?c.x+c.w-5:c.x+5,y+15,{align:c.align}));doc.setTextColor(0);y+=22}
  tableHeader()
  for(const line of lines){
    const desc=line.description||line.product_name_snapshot||'Item',sku=line.sku_snapshot?` (${line.sku_snapshot})`:''
    const qty=Number(line.quantity||0),unit=Number(line.unit_price||0)
    const total=line.line_total!=null?Number(line.line_total):qty*unit-Number(line.discount_amount||0)+Number(line.tax_amount||0)+Number(line.customer_payment_fee_allocated||0)
    const descLines=doc.splitTextToSize(`${desc}${sku}`,255),height=Math.max(rowH,descLines.length*10+10)
    if(y+height>H-92){y=newPage();tableHeader()}
    doc.setDrawColor(230);doc.line(margin,y+height,W-margin,y+height)
    doc.setFont('helvetica','normal');doc.setFontSize(8);doc.text(descLines,margin+5,y+15)
    doc.text(String(qty),355,y+15,{align:'right'});doc.text(money(unit,currency),445,y+15,{align:'right'});doc.text(money(total,currency),W-margin-5,y+15,{align:'right'})
    y+=height
  }

  y+=16
  if(y>H-220)y=newPage()
  const totals=[['Subtotal',record.subtotal],['Discount',record.discount_total],['Tax',record.tax_total],['Shipping',record.shipping_total],['Payment Fee',record.customer_payment_fee_total]]
  if(type==='Invoice'&&record.balance_due!=null)totals.push(['Balance Due',record.balance_due])
  const totalValue=type==='Order'?record.customer_total:record.total
  totals.push([type==='Invoice'?'Invoice Total':`${type} Total`,totalValue])
  const tx=W-margin-190
  totals.filter(([,v])=>v!=null&&Number(v)!==0 || String(v)==='0').forEach(([label,value],idx,arr)=>{const last=idx===arr.length-1;doc.setFont('helvetica',last?'bold':'normal');doc.setFontSize(last?11:9);if(last){doc.setDrawColor(...accent);doc.line(tx,y-7,W-margin,y-7)}doc.text(label,tx,y);doc.text(money(value,currency),W-margin,y,{align:'right'});y+=last?22:17})

  const details=[]
  if(record.shipping_service_name||record.shipping_carrier)details.push(`Shipping: ${[record.shipping_carrier,record.shipping_service_name].filter(Boolean).join(' / ')}`)
  if(record.notes)details.push(`Notes: ${record.notes}`)
  if(type==='Invoice'&&order?.order_number)details.push(`Related order: ${order.order_number}`)
  if(details.length){if(y>H-115)y=newPage();doc.setFont('helvetica','bold');doc.setFontSize(8);doc.setTextColor(...accent);doc.text('DETAILS',margin,y);doc.setTextColor(0);y=addLines(doc,details,margin,y+16,{fontSize:8,lineHeight:11,maxWidth:W-margin*2})}

  footer()
  doc.save(filename)
}

export async function downloadQuotePdf(tenantId,quoteId){
  const[q,l]=await Promise.all([
    supabase.from('quotes').select('*').eq('tenant_id',tenantId).eq('id',quoteId).single(),
    supabase.from('quote_lines').select('*').eq('tenant_id',tenantId).eq('quote_id',quoteId).order('created_at')
  ])
  if(q.error)throw q.error;if(l.error)throw l.error
  const{account,contact}=await loadCustomer(tenantId,q.data.account_id,q.data.contact_id)
  return renderCommercialPdf({tenantId,type:'Quote',record:q.data,lines:l.data||[],account,contact})
}

export async function downloadOrderPdf(tenantId,orderId){
  const[o,l]=await Promise.all([
    supabase.from('orders').select('*').eq('tenant_id',tenantId).eq('id',orderId).single(),
    supabase.from('order_lines').select('*').eq('tenant_id',tenantId).eq('order_id',orderId).order('created_at')
  ])
  if(o.error)throw o.error;if(l.error)throw l.error
  const{account,contact}=await loadCustomer(tenantId,o.data.account_id,o.data.contact_id)
  return renderCommercialPdf({tenantId,type:'Order',record:o.data,lines:l.data||[],account,contact})
}

export async function downloadInvoicePdf(tenantId,invoiceId){
  const[i,l]=await Promise.all([
    supabase.from('invoices').select('*').eq('tenant_id',tenantId).eq('id',invoiceId).single(),
    supabase.from('invoice_lines').select('*').eq('tenant_id',tenantId).eq('invoice_id',invoiceId).order('created_at')
  ])
  if(i.error)throw i.error;if(l.error)throw l.error
  const{account,contact}=await loadCustomer(tenantId,i.data.account_id,i.data.contact_id)
  let order=null
  if(i.data.order_id){const{o,error}=await supabase.from('orders').select('*').eq('tenant_id',tenantId).eq('id',i.data.order_id).maybeSingle();if(error)throw error;order=o}
  return renderCommercialPdf({tenantId,type:'Invoice',record:i.data,lines:l.data||[],account,contact,order})
}
